import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { LinkContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat';
import { Chip } from '@trinity/chat-components/components/presentation/Chip';
import { SearchInput } from '@trinity/chat-components/components/presentation/Input.Search';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ChatHistoryManagerContext, ChatHistoryItemsContext } from './context';
import { ChatHistoryItemContainer } from './Item.Container';

type Props = unknown;

export const ChatHistorySearch = (props: Props) => {
  const [filter, setFilter] = useState<'all' | 'bookmarked' | 'profile'>('all');
  const [name, setName] = useState<string>('');
  const historyManager = useContext(ChatHistoryManagerContext);
  const historyItems = useContext(ChatHistoryItemsContext);
  const chat = useContext(ActiveChatSessionContext);
  const activeBrandIdentifier = useActiveBrandIdentifier();
  const link = useContext(LinkContext);

  const getIncludeAdditional = useCallback(() => {
    if (!link?.initialized && link?.chatIdentifier && !historyItems.map(i => i.identifier).includes(link.chatIdentifier)) {
      return [link.chatIdentifier];
    }
    return null;
  }, [historyItems, link?.initialized, link?.chatIdentifier]);

  const queryKey = useMemo(() => {
    const mode: 'page' | 'all' = filter === 'profile' ? 'page' : 'all';
    return {
      mode,
      cursor: null,
      pageSize: 20,
      includeAdditional: getIncludeAdditional(),
      context: chat.context,
      brandIdentifier: activeBrandIdentifier,
      name: name.trim(),
      isBookmarked: filter === 'bookmarked',
    };
  }, [historyManager.mode, historyManager.cursor, getIncludeAdditional, chat.context, activeBrandIdentifier, name, filter]);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: [`chat:get:history`, queryKey] as const,
    queryFn: ({ queryKey }) => {
      // dispatch({ type: 'chat-loading-started' });
      return apiClient.chat.searchChatHistory({
        mode: queryKey[1].mode,
        cursor: queryKey[1].cursor,
        pageSize: queryKey[1].pageSize,
        includeAdditional: queryKey[1].includeAdditional,
        context: queryKey[1].context,
        brandIdentifier: queryKey[1].brandIdentifier,
        name: queryKey[1].name,
        isBookmarked: queryKey[1].isBookmarked,
      }).then(data => {
        const _cursor = data.items.length ? data.items[data.items.length - 1].id : null;
        // dispatch({
        //   type: queryKey[1].cursor ? 'chat-history-page-loaded' : 'chat-history-loaded',
        //   payload: {
        //     cursor,
        //     hasMore: data.hasMore,
        //     items: data.items,
        //     shared: data.shared,
        //   },
        // });
        return data;
      });
    },
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  const handleClickFilter = useCallback((value: 'all' | 'bookmarked' | 'profile') => {
    if (value === filter) {
      setFilter('all');
    } else if (value === 'profile') {
      setFilter('profile');
    } else {
      setFilter(value);
    }
  }, [filter]);

  return (
    <Root>
      <Wrap>
        <div style={{ border: '1px solid var(--gray)', borderRadius: 8, marginBottom: 8 }}>
          <SearchInput
            clearable={name.length > 0}
            placeholder="Search"
            onChange={val => setName(val)}
            value={name} />
        </div>
        <FilterButtons>
          <ChipStyled
            selected={filter === 'all'}
            onClick={() => handleClickFilter('all')}>
            All
          </ChipStyled>
          <ChipStyled
            selected={filter === 'bookmarked'}
            onClick={() => handleClickFilter('bookmarked')}>
            Pinned
          </ChipStyled>
          <ChipStyled
            selected={filter === 'profile'}
            onClick={() => handleClickFilter('profile')}>
            Pinned
          </ChipStyled>
        </FilterButtons>
      </Wrap>
      {query.data?.items.map(item => (
        <ChatHistoryItemContainer
          key={item.id}
          item={item} />
      ))}
    </Root>
  );
};

ChatHistorySearch.displayName = 'Chat.History.Search';

const Root = styled.div`
  width: 100%;
`;

const Wrap = styled.div`
  margin: 15px;
`;

const FilterButtons = styled.div(({ theme }) => ({
  display: 'flex',
  gap: 8,
}));

const ChipStyled = styled(Chip)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 60,
}));
